import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';

/**
 * T059: useSuggestions hook.
 * Fetch suggestions for a product and mutation to mark as actioned.
 */

interface Suggestion {
  id: string;
  category: string;
  severity: string;
  affectedField: string;
  title: string;
  detail: string;
  recommendedAction: string;
  estimatedImpact: number;
  isActioned: boolean;
  actionedAt: string | null;
  isBlurred: boolean;
}

interface SuggestionListResponse {
  suggestions: Suggestion[];
  totalCount: number;
  actionedCount: number;
}

export function useSuggestions(productId: string | undefined) {
  return useQuery<SuggestionListResponse>({
    queryKey: ['product', productId, 'suggestions'],
    queryFn: () => api.get<SuggestionListResponse>(`/products/${productId}/suggestions`),
    enabled: !!productId,
    staleTime: 30_000,
  });
}

export function useMarkSuggestionActioned(productId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (suggestionId: string) =>
      api.post<{ id: string; isActioned: boolean; actionedAt: string }>(
        `/products/${productId}/suggestions/${suggestionId}/action`,
      ),
    onMutate: async (suggestionId) => {
      // Optimistic update
      const queryKey = ['product', productId, 'suggestions'];
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<SuggestionListResponse>(queryKey);

      if (previous) {
        queryClient.setQueryData<SuggestionListResponse>(queryKey, {
          ...previous,
          suggestions: previous.suggestions.map((s) =>
            s.id === suggestionId
              ? { ...s, isActioned: true, actionedAt: new Date().toISOString() }
              : s,
          ),
          actionedCount: previous.actionedCount + 1,
        });
      }

      return { previous };
    },
    onError: (_err, _suggestionId, context) => {
      // Rollback on error
      if (context?.previous) {
        queryClient.setQueryData(
          ['product', productId, 'suggestions'],
          context.previous,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['product', productId, 'suggestions'] });
    },
  });
}
